import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';
import { FirebaseGoogleSignIn } from '../../../components/googleSingIn/googleSingIn';
import { InAppBrowserBanner } from '../../../components/InAppBrowserBanner/InAppBrowserBanner';
import { isInAppBrowser } from '../../../shared/lib/browser/in-app-browser';
import styles from './styles.module.css';
import Button from '../../../shared/ui/button/button';
import ContourBackdrop from '../../../components/ContourBackdrop/ContourBackdrop';
import { singInRequest, verifyMFARequest } from '../api';
import { useLocale } from '../../../context/LocaleContext';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../../../context/AuthContext';
import {
	formatErrorDetail,
	isNetworkError,
} from '../../../shared/api/apiError';

export const SignIn = () => {
	const intl = useIntl();
	const { setErrorModalShow } = useLocale();
	const navigate = useNavigate();
	const { onLogin, setIsAuth } = useAuth();
	const [icon, setIcon] = useState(faEyeSlash);
	const [type, setType] = useState('password');
	const [isToastOpen, setIsToastOpen] = useState(false);
	const [errorMessage, seterrorMessage] = useState([]);
	const [errors, setErrors] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [mfaRequired, setMfaRequired] = useState(false);
	const [mfaData, setMfaData] = useState(null);
	const [mfaCodeInputs, setMfaCodeInputs] = useState(['', '', '', '', '', '']);
	const [mfaError, setMfaError] = useState('');
	const [formData, setFormData] = useState({
		email: '',
		password: '',
	});
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

	const validateField = (name, value) => {
		switch (name) {
			case 'email':
				if (!value.trim())
					return intl.formatMessage({ id: 'SignInValidationId1' });
				if (!emailRegex.test(value))
					return intl.formatMessage({ id: 'SignInValidationId2' });
				return '';
			case 'password':
				if (!value.trim())
					return intl.formatMessage({ id: 'SignInValidationId3' });
				return '';

			default:
				return '';
		}
	};
	const validateAllFields = () => {
		const newErrors = {};

		Object.keys(formData).forEach(key => {
			const error = validateField(key, formData[key]);
			if (error) {
				newErrors[key] = error;
			}
		});

		return newErrors;
	};
	const handleToggle = () => {
		if (type === 'password') {
			setIcon(faEye);
			setType('text');
		} else {
			setIcon(faEyeSlash);
			setType('password');
		}
	};
	const handleInputChange = e => {
		const { name, value, type, checked } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value,
		}));
		if (errors[name]) {
			setErrors(prev => ({
				...prev,
				[name]: '',
			}));
		}
	};

	const handleSubmit = async e => {
		e.preventDefault();
		setIsLoading(true);
		seterrorMessage('');
		setMfaError('');

		const userData = {
			email: formData.email.trim(),
			password: formData.password,
			// The form no longer offers the choice, so keep the short session the
			// unticked checkbox used to send.
			remember_me: false,
		};

		const validationErrors = validateAllFields();

		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			setIsLoading(false);
			return;
		}

		try {
			const sigIn = await singInRequest(userData);

			// Server unreachable / network error – axios error without a response
			if (isNetworkError(sigIn)) {
				seterrorMessage(intl.formatMessage({ id: 'SignInValidationId5' }));
				setIsLoading(false);
				return;
			}

			// Check if response is an error
			if (sigIn && sigIn.response) {
				seterrorMessage(
					formatErrorDetail(
						sigIn.response?.data?.detail,
						intl.formatMessage({ id: 'SignInValidationId4' }),
					),
				);
				setIsLoading(false);
				return;
			}

			// Check if MFA is required
			if (sigIn && sigIn.requires_mfa) {
				setMfaRequired(true);
				setMfaData({
					temp_token: sigIn.temp_token,
					mfa_type: sigIn.mfa_type,
					user: sigIn.user,
					authProvider: 'email',
				});
				setMfaCodeInputs(['', '', '', '', '', '']);
				setIsLoading(false);
				return;
			}

			// Normal login success
			if (sigIn && sigIn.access_token) {
				window.localStorage.setItem('accessToken', sigIn.access_token);
				window.localStorage.setItem('authProvider', 'email');
				await onLogin(userData);
				navigate('/overview');
			} else {
				seterrorMessage(intl.formatMessage({ id: 'SignInValidationId5' }));
			}
		} catch (err) {
			console.error(err);
			seterrorMessage(intl.formatMessage({ id: 'SignInValidationId5' }));
		} finally {
			setIsLoading(false);
		}
	};

	const mfaCode = mfaCodeInputs.join('');

	const handleMfaCodeInputChange = (index, value) => {
		const digit = value.replace(/\D/g, '').slice(0, 1);
		const newInputs = [...mfaCodeInputs];
		newInputs[index] = digit;
		setMfaCodeInputs(newInputs);
		setMfaError('');
		if (digit && index < 5) {
			const nextInput = document.getElementById(`mfa-code-input-${index + 1}`);
			if (nextInput) nextInput.focus();
		}
	};

	const handleMfaCodeInputKeyDown = (index, e) => {
		if (e.key === 'Backspace' && !mfaCodeInputs[index] && index > 0) {
			const prevInput = document.getElementById(`mfa-code-input-${index - 1}`);
			if (prevInput) prevInput.focus();
		}
	};

	const handleMfaPasteCode = e => {
		e.preventDefault();
		const pastedData = e.clipboardData
			.getData('text')
			.replace(/\D/g, '')
			.slice(0, 6);
		if (pastedData.length > 0) {
			const newInputs = ['', '', '', '', '', ''];
			for (let i = 0; i < pastedData.length && i < 6; i++) {
				newInputs[i] = pastedData[i];
			}
			setMfaCodeInputs(newInputs);
			setMfaError('');
			const nextIndex = Math.min(pastedData.length, 5);
			const nextInput = document.getElementById(`mfa-code-input-${nextIndex}`);
			if (nextInput) nextInput.focus();
		}
	};

	const handleMFAVerify = async e => {
		e.preventDefault();
		setIsLoading(true);
		setMfaError('');

		if (!mfaCode || mfaCode.length !== 6 || !/^\d{6}$/.test(mfaCode)) {
			setMfaError(intl.formatMessage({ id: 'SignInValidationId6' }));
			setIsLoading(false);
			return;
		}

		try {
			const result = await verifyMFARequest(
				mfaData.temp_token,
				mfaCode,
				mfaData.mfa_type,
			);

			if (result && result.response) {
				const errorData = result.response?.data?.detail;
				setMfaError(
					typeof errorData === 'string'
						? errorData
						: intl.formatMessage({ id: 'SignInValidationId9' }),
				);
				setIsLoading(false);
				return;
			}

			if (result && result.access_token) {
				const authProvider = mfaData.authProvider || 'email';
				window.localStorage.setItem('accessToken', result.access_token);
				window.localStorage.setItem('authProvider', authProvider);

				if (result.mfa_session_token) {
					window.localStorage.setItem(
						'mfaSessionToken',
						result.mfa_session_token,
					);
				}

				setIsAuth(true);
				navigate('/overview');
			} else {
				setMfaError(intl.formatMessage({ id: 'SignInValidationId7' }));
			}
		} catch (err) {
			console.error(err);
			setMfaError(intl.formatMessage({ id: 'SignInValidationId8' }));
		} finally {
			setIsLoading(false);
		}
	};

	const handleBackToLogin = () => {
		setMfaRequired(false);
		setMfaData(null);
		setMfaCodeInputs(['', '', '', '', '', '']);
		setMfaError('');
	};

	return (
		<div className={styles.container}>
			{/* The landing page's contour artwork, framing the card. */}
			<ContourBackdrop className={styles.contoursLeft} rings={8} from={1.6} />
			<ContourBackdrop className={styles.contoursRight} rings={6} from={2.2} />

			<h1 className={styles.welcome}>
				{intl.formatMessage({ id: 'AuthId1' })}
			</h1>

			<div className={styles.card}>
				<h2 className={styles.cardTitle}>
					{intl.formatMessage({ id: 'SignInId10' })}
				</h2>

				{/* <form onSubmit={handleSubmit}> */}
				<div className={styles.form}>
					<div className={styles.inputGroup}>
						<label htmlFor='email' className={styles.label}>
							{intl.formatMessage({ id: 'SignInId3' })}
						</label>
						<input
							type='email'
							id='email'
							name='email'
							value={formData.email}
							onChange={handleInputChange}
							className={styles.input}
							placeholder={intl.formatMessage({
								id: 'SignInId4',
							})}
							required
						/>
						{errors.email && (
							<span className={styles.errorText}>{errors.email}</span>
						)}
					</div>

					<div className={styles.inputGroup}>
						<label htmlFor='password' className={styles.label}>
							{intl.formatMessage({ id: 'SignInId5' })}
						</label>
						{/* Wrapper so the eye icon centres on the field, not on
                label + field together. */}
						<div className={styles.passwordWrap}>
							<input
								type={type}
								id='password'
								name='password'
								value={formData.password}
								onChange={handleInputChange}
								className={styles.input}
								placeholder={intl.formatMessage({
									id: 'SignInId6',
								})}
								required
							/>
							<span className={styles.eyeBox} onClick={handleToggle}>
								<FontAwesomeIcon icon={icon} />
							</span>
						</div>
						{errors.password && (
							<span className={styles.errorText}>{errors.password}</span>
						)}
					</div>
					{errorMessage ? (
						<span className={styles.errorText}>{errorMessage}</span>
					) : (
						''
					)}

					<Button
						type='submit'
						fullWidth
						onClick={handleSubmit}
						loading={isLoading}
					>
						{isLoading
							? intl.formatMessage({ id: 'SignInId9' })
							: intl.formatMessage({ id: 'SignInId10' })}
					</Button>
					{useMemo(() => isInAppBrowser(), []) && <InAppBrowserBanner />}
					<div className={styles.linelBlock}>
						<div></div>
						<p>{intl.formatMessage({ id: 'SignInId11' })}</p>
						<div></div>
					</div>

					<FirebaseGoogleSignIn
						isInAppBrowser={useMemo(() => isInAppBrowser(), [])}
						onInAppBrowserGoogleClick={() =>
							toast(
								intl.formatMessage({ id: 'InAppBrowserBannerToastMessage' }),
								{
									icon: '⚠️',
									duration: 5000,
								},
							)
						}
						onMfaRequired={data => {
							setMfaRequired(true);
							setMfaData(data);
							setMfaCodeInputs(['', '', '', '', '', '']);
						}}
					/>
					{/* </form> */}
				</div>
				<div className={styles.footer}>
					<p className={styles.footerText}>
						{intl.formatMessage({ id: 'SignInId12' })}
						<Link to='/sign-up' className={styles.link}>
							{intl.formatMessage({ id: 'SignInId13' })}
						</Link>
					</p>
				</div>
			</div>

			{/* MFA verification popup – same pattern as profile "enable MFA" modal */}
			{mfaRequired && mfaData && (
				<div className={styles.mfaModal}>
					<div className={styles.mfaModalContent}>
						<div className={styles.mfaModalHeader}>
							<h3 className={styles.mfaModalTitle}>
								{intl.formatMessage({ id: 'SignInId14' })}
							</h3>
							<button
								type='button'
								className={styles.mfaModalClose}
								onClick={handleBackToLogin}
								disabled={isLoading}
								aria-label='Close'
							>
								<X size={20} />
							</button>
						</div>
						<h4 className={styles.mfaModalSubtitle}>
							{intl.formatMessage({ id: 'SignInId15' })}
						</h4>
						<p className={styles.mfaModalDescription}>
							{mfaData.mfa_type === 'email'
								? intl.formatMessage({ id: 'SignInId16' })
								: intl.formatMessage({ id: 'SignInId17' })}
						</p>
						<div className={styles.codeInputContainer}>
							{mfaCodeInputs.map((value, index) => (
								<input
									key={index}
									id={`mfa-code-input-${index}`}
									type='text'
									inputMode='numeric'
									maxLength={1}
									value={value}
									onChange={e =>
										handleMfaCodeInputChange(index, e.target.value)
									}
									onKeyDown={e => handleMfaCodeInputKeyDown(index, e)}
									onPaste={handleMfaPasteCode}
									className={styles.codeInput}
									autoFocus={index === 0}
								/>
							))}
						</div>
						{mfaData.mfa_type !== 'email' && (
							<p className={styles.mfaCodeInfo}>
								{intl.formatMessage({ id: 'SignInId18' })}
							</p>
						)}
						{mfaError && (
							<span className={styles.mfaErrorText}>{mfaError}</span>
						)}
						<div className={styles.mfaModalButtons}>
							<Button
								fullWidth
								onClick={handleMFAVerify}
								disabled={isLoading || mfaCode.length !== 6}
							>
								{isLoading
									? intl.formatMessage({ id: 'SignInId19' })
									: intl.formatMessage({ id: 'SignInId20' })}
							</Button>
							<Button
								variant='secondary'
								fullWidth
								onClick={handleBackToLogin}
								disabled={isLoading}
							>
								{intl.formatMessage({ id: 'SignInId21' })}
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};
